import { FlashList } from '@shopify/flash-list';
import * as React from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { JobTimesheetData } from './types';
import { Small } from '../ui/typography';

const MIN_COLUMN_WIDTHS = [50, 50, 50, 50];

interface TurnedDownInsideTableProps {
  timesheetData: JobTimesheetData;
}

export default function TurnedDownInsideTable({ timesheetData }: TurnedDownInsideTableProps) {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const columnWidths = React.useMemo(() => {
    return MIN_COLUMN_WIDTHS.map((minWidth) => {
      const evenWidth = width / MIN_COLUMN_WIDTHS.length;
      return evenWidth > minWidth ? evenWidth : minWidth;
    });
  }, [width]);

  return (
    <ScrollView 
      horizontal 
      bounces={false} 
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      style={{ flex: 1 }}
      contentContainerStyle={{ flexGrow: 1 }}
      nestedScrollEnabled={true}
    >
      <Table aria-labelledby='invoice-table'>
        <TableHeader>
          <TableRow>
            <TableHead className='px-0.5' style={{ width: columnWidths[0] }}>
              <Small>Date</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[1] }}>
              <Small>Time</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[2] }}>
              <Small>Hours</Small>
            </TableHead>
            <TableHead style={{ width: columnWidths[3] }}>
              <Small className='text-center md:text-right md:pr-5'>Total</Small>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!timesheetData.rows.length && (
            <TableRow>
              <TableCell>
                <Small className='text-gray-500'>No turned down outside 24 hours</Small>
              </TableCell>
            </TableRow>
          )}
          <FlashList
            data={timesheetData.rows}
            scrollEnabled={false}
            contentContainerStyle={{
              paddingBottom: insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            renderItem={({ item: timesheet, index }) => {
              return (
                <TableRow
                  key={index}
                  className={cn('active:bg-secondary', index % 2 && 'bg-muted/40 ')}
                >
                  <TableCell style={{ width: columnWidths[0] }}>
                    <Small>{timesheet.date}</Small>
                  </TableCell>
                  <TableCell style={{ width: columnWidths[1] }}>
                    <Small>{timesheet.time}</Small>
                  </TableCell>
                  <TableCell style={{ width: columnWidths[2] }}>
                    <Small>{timesheet.hours}</Small>
                  </TableCell>
                  <TableCell style={{ width: columnWidths[3] }} className='items-end '>
                    <Small>{timesheet.rowTotal}</Small>
                  </TableCell>
                </TableRow>
              );
            }}
            ListFooterComponent={() => {
              return (
                <>
                  <TableFooter>
                    <TableRow>
                      <TableCell className='flex-1 justify-center'>
                        <Small className='text-foreground'>Total</Small>
                      </TableCell>
                      <TableCell className='items-end pr-8'>
                        <Small>£{timesheetData.total}</Small>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </>
              );
            }}
          />
        </TableBody>
      </Table>
    </ScrollView>
  );
}